/**
 * Modern Scheduling Dashboard - 2025 Design
 * Unified interface with tabbed navigation for Staff, Calendar, and Jobs
 */

import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Calendar,
  Users,
  Clock,
  AlertTriangle,
  CheckCircle2,
  TrendingUp,
  Cloud,
  Zap,
  BarChart3,
  Plus,
  CalendarDays,
  Briefcase
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog';
import { useSchedules, useStaff, useConflicts } from '../hooks/use-scheduling';
import { Link } from 'wouter';
import StaffManagement from './StaffManagement';
import SchedulingCalendar from './SchedulingCalendar';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';

export default function SchedulingDashboard() {
  const [activeTab, setActiveTab] = useState('overview');
  const [isCreateJobOpen, setIsCreateJobOpen] = useState(false);

  const { data: schedules = [], isLoading: schedulesLoading } = useSchedules();
  const { data: staff = [], isLoading: staffLoading } = useStaff();
  const { data: conflictsData, isLoading: conflictsLoading } = useConflicts({ resolutionStatus: 'unresolved' });

  // Ensure conflicts is always an array
  const conflicts = Array.isArray(conflictsData) ? conflictsData : [];

  // Calculate stats
  const todaySchedules = Array.isArray(schedules) ? schedules.filter((s: any) => {
    const today = new Date().toISOString().split('T')[0];
    return s.scheduledDate === today && s.status === 'scheduled';
  }) : [];

  const upcomingSchedules = Array.isArray(schedules) ? schedules.filter((s: any) => {
    const today = new Date().toISOString().split('T')[0];
    return s.scheduledDate > today && s.status === 'scheduled';
  }) : [];

  const activeStaff = Array.isArray(staff) ? staff.filter((s: any) => s.isActive) : [];
  const criticalConflicts = conflicts.filter((c: any) => c.severity === 'critical');

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="border-b border-white/20 backdrop-blur-xl bg-white/40 dark:bg-slate-900/40 sticky top-0 z-50"
      >
        <div className="container mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                Installation Scheduling
              </h1>
              <p className="text-slate-600 dark:text-slate-400 mt-1">
                Manage your team and installations efficiently
              </p>
            </div>

            <Dialog open={isCreateJobOpen} onOpenChange={setIsCreateJobOpen}>
              <DialogTrigger asChild>
                <Button className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Job
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Create New Installation Job</DialogTitle>
                </DialogHeader>
                <CreateJobDialog onClose={() => setIsCreateJobOpen(false)} />
              </DialogContent>
            </Dialog>
          </div>

          {/* Main Navigation Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-6">
            <TabsList className="backdrop-blur-xl bg-white/60 dark:bg-slate-900/60 border-0 shadow-lg p-1">
              <TabsTrigger value="overview" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-600 data-[state=active]:to-indigo-600 data-[state=active]:text-white gap-2">
                <BarChart3 className="w-4 h-4" />
                Overview
              </TabsTrigger>
              <TabsTrigger value="calendar" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-600 data-[state=active]:to-indigo-600 data-[state=active]:text-white gap-2">
                <CalendarDays className="w-4 h-4" />
                Calendar
              </TabsTrigger>
              <TabsTrigger value="staff" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-600 data-[state=active]:to-indigo-600 data-[state=active]:text-white gap-2">
                <Users className="w-4 h-4" />
                Staff
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </motion.div>

      {/* Tab Contents */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="container mx-auto px-6 py-8">
        {/* Overview Tab */}
        <TabsContent value="overview" className="mt-0 space-y-8">
          {/* Stats Grid */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
          >
          {/* Today's Jobs */}
          <Card className="border-0 shadow-xl backdrop-blur-xl bg-white/60 dark:bg-slate-900/60 hover:scale-105 transition-transform duration-300">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-slate-600 dark:text-slate-400">
                Today's Jobs
              </CardTitle>
              <div className="p-2 rounded-lg bg-blue-500/10">
                <Calendar className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-blue-400 bg-clip-text text-transparent">
                {todaySchedules.length}
              </div>
              <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">
                {todaySchedules.length === 0 ? 'No installations scheduled' : 'installations scheduled'}
              </p>
            </CardContent>
          </Card>

          {/* Active Staff */}
          <Card className="border-0 shadow-xl backdrop-blur-xl bg-white/60 dark:bg-slate-900/60 hover:scale-105 transition-transform duration-300">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-slate-600 dark:text-slate-400">
                Active Staff
              </CardTitle>
              <div className="p-2 rounded-lg bg-green-500/10">
                <Users className="w-5 h-5 text-green-600 dark:text-green-400" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold bg-gradient-to-r from-green-600 to-green-400 bg-clip-text text-transparent">
                {activeStaff.length}
              </div>
              <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">
                team members available
              </p>
            </CardContent>
          </Card>

          {/* Upcoming Jobs */}
          <Card className="border-0 shadow-xl backdrop-blur-xl bg-white/60 dark:bg-slate-900/60 hover:scale-105 transition-transform duration-300">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-slate-600 dark:text-slate-400">
                Upcoming
              </CardTitle>
              <div className="p-2 rounded-lg bg-indigo-500/10">
                <TrendingUp className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold bg-gradient-to-r from-indigo-600 to-indigo-400 bg-clip-text text-transparent">
                {upcomingSchedules.length}
              </div>
              <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">
                jobs in pipeline
              </p>
            </CardContent>
          </Card>

          {/* Conflicts */}
          <Card className="border-0 shadow-xl backdrop-blur-xl bg-white/60 dark:bg-slate-900/60 hover:scale-105 transition-transform duration-300">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-slate-600 dark:text-slate-400">
                Conflicts
              </CardTitle>
              <div className="p-2 rounded-lg bg-red-500/10">
                <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold bg-gradient-to-r from-red-600 to-red-400 bg-clip-text text-transparent">
                {criticalConflicts.length}
              </div>
              <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">
                {criticalConflicts.length === 0 ? 'all clear' : 'need attention'}
              </p>
            </CardContent>
          </Card>
        </motion.div>

          {/* Jobs Sub-Tabs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Tabs defaultValue="today" className="space-y-6">
              <TabsList className="backdrop-blur-xl bg-white/60 dark:bg-slate-900/60 border-0 shadow-lg p-1">
                <TabsTrigger value="today" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-600 data-[state=active]:to-indigo-600 data-[state=active]:text-white">
                  Today's Schedule
                </TabsTrigger>
                <TabsTrigger value="upcoming">Upcoming Jobs</TabsTrigger>
                <TabsTrigger value="conflicts">Conflicts</TabsTrigger>
              </TabsList>

            {/* Today's Schedule */}
            <TabsContent value="today" className="space-y-4">
              {todaySchedules.length === 0 ? (
                <Card className="border-0 shadow-xl backdrop-blur-xl bg-white/60 dark:bg-slate-900/60">
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <CheckCircle2 className="w-16 h-16 text-green-500 mb-4" />
                    <h3 className="text-xl font-semibold mb-2">All Clear Today!</h3>
                    <p className="text-slate-600 dark:text-slate-400">No installations scheduled for today</p>
                  </CardContent>
                </Card>
              ) : (
                todaySchedules.map((schedule: any, index: number) => (
                  <motion.div
                    key={schedule.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Card className="border-0 shadow-xl backdrop-blur-xl bg-white/60 dark:bg-slate-900/60 hover:shadow-2xl transition-all duration-300">
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <Badge variant="outline" className="bg-blue-500/10 text-blue-600 border-blue-200">
                                {schedule.priority}
                              </Badge>
                              <span className="text-sm text-slate-600 dark:text-slate-400">
                                {schedule.scheduledStartTime} - {schedule.scheduledEndTime}
                              </span>
                            </div>
                            <h4 className="font-semibold text-lg mb-1">
                              Installation #{schedule.installationId}
                            </h4>
                            <p className="text-slate-600 dark:text-slate-400 text-sm">
                              {schedule.installation?.installationAddress || 'Address not specified'}
                            </p>
                            <div className="flex items-center gap-4 mt-3">
                              <div className="flex items-center gap-1 text-sm">
                                <Users className="w-4 h-4" />
                                <span>{schedule.assignments?.length || 0} / {schedule.crewSizeRequired} crew</span>
                              </div>
                              <div className="flex items-center gap-1 text-sm">
                                <Zap className="w-4 h-4" />
                                <span>{schedule.installation?.systemSize || 'N/A'}</span>
                              </div>
                            </div>
                          </div>
                          <Link href={`/scheduling/jobs/${schedule.id}`}>
                            <Button variant="ghost" size="sm">View Details</Button>
                          </Link>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))
              )}
            </TabsContent>

            {/* Upcoming Jobs */}
            <TabsContent value="upcoming" className="space-y-4">
              {upcomingSchedules.length === 0 ? (
                <Card className="border-0 shadow-xl backdrop-blur-xl bg-white/60 dark:bg-slate-900/60">
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <Calendar className="w-16 h-16 text-slate-400 mb-4" />
                    <h3 className="text-xl font-semibold mb-2">No Upcoming Jobs</h3>
                    <p className="text-slate-600 dark:text-slate-400 mb-4">Schedule new installations to get started</p>
                    <Link href="/scheduling/calendar">
                      <Button className="gap-2">
                        <Plus className="w-4 h-4" />
                        Schedule Job
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              ) : (
                upcomingSchedules.slice(0, 10).map((schedule: any, index: number) => (
                  <motion.div
                    key={schedule.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Card className="border-0 shadow-xl backdrop-blur-xl bg-white/60 dark:bg-slate-900/60 hover:shadow-2xl transition-all duration-300">
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <Badge variant="outline" className="bg-indigo-500/10 text-indigo-600 border-indigo-200">
                                {new Date(schedule.scheduledDate).toLocaleDateString('en-GB', {
                                  weekday: 'short',
                                  month: 'short',
                                  day: 'numeric'
                                })}
                              </Badge>
                              <span className="text-sm text-slate-600 dark:text-slate-400">
                                {schedule.scheduledStartTime} - {schedule.scheduledEndTime}
                              </span>
                            </div>
                            <h4 className="font-semibold text-lg mb-1">
                              Installation #{schedule.installationId}
                            </h4>
                            <p className="text-slate-600 dark:text-slate-400 text-sm">
                              {schedule.installation?.installationAddress || 'Address not specified'}
                            </p>
                          </div>
                          <Link href={`/scheduling/jobs/${schedule.id}`}>
                            <Button variant="ghost" size="sm">View</Button>
                          </Link>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))
              )}
            </TabsContent>

            {/* Conflicts */}
            <TabsContent value="conflicts" className="space-y-4">
              {conflicts.length === 0 ? (
                <Card className="border-0 shadow-xl backdrop-blur-xl bg-white/60 dark:bg-slate-900/60">
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <CheckCircle2 className="w-16 h-16 text-green-500 mb-4" />
                    <h3 className="text-xl font-semibold mb-2">No Conflicts!</h3>
                    <p className="text-slate-600 dark:text-slate-400">All schedules are conflict-free</p>
                  </CardContent>
                </Card>
              ) : (
                conflicts.map((conflict: any, index: number) => (
                  <motion.div
                    key={conflict.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Card className={`border-0 shadow-xl backdrop-blur-xl transition-all duration-300 ${
                      conflict.severity === 'critical'
                        ? 'bg-red-50/80 dark:bg-red-950/20 border-l-4 border-l-red-500'
                        : conflict.severity === 'error'
                        ? 'bg-orange-50/80 dark:bg-orange-950/20 border-l-4 border-l-orange-500'
                        : 'bg-yellow-50/80 dark:bg-yellow-950/20 border-l-4 border-l-yellow-500'
                    }`}>
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <AlertTriangle className={`w-5 h-5 ${
                                conflict.severity === 'critical' ? 'text-red-600' :
                                conflict.severity === 'error' ? 'text-orange-600' :
                                'text-yellow-600'
                              }`} />
                              <Badge variant="outline" className={
                                conflict.severity === 'critical' ? 'bg-red-500/10 text-red-600 border-red-200' :
                                conflict.severity === 'error' ? 'bg-orange-500/10 text-orange-600 border-orange-200' :
                                'bg-yellow-500/10 text-yellow-600 border-yellow-200'
                              }>
                                {conflict.severity}
                              </Badge>
                              <span className="text-sm text-slate-600 dark:text-slate-400">
                                {conflict.conflictType.replace(/_/g, ' ')}
                              </span>
                            </div>
                            <p className="text-slate-700 dark:text-slate-300">
                              {conflict.description}
                            </p>
                          </div>
                          <Button variant="outline" size="sm" className="ml-4">
                            Resolve
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))
              )}
            </TabsContent>
            </Tabs>
          </motion.div>
        </TabsContent>

        {/* Calendar Tab */}
        <TabsContent value="calendar" className="mt-0">
          <SchedulingCalendar />
        </TabsContent>

        {/* Staff Tab */}
        <TabsContent value="staff" className="mt-0">
          <StaffManagement />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Create Job Dialog Component
function CreateJobDialog({ onClose }: { onClose: () => void }) {
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
  const [selectedStaff, setSelectedStaff] = useState<number[]>([]);
  const [jobDate, setJobDate] = useState('');
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('17:00');

  // Fetch Won customers - using exact same endpoint as site surveys
  // This ensures consistency and reuses the working forSiteSurvey logic
  const { data: wonCustomers = [], isLoading: customersLoading, error: customersError } = useQuery({
    queryKey: ['/api/installations', 'forScheduling'],
    queryFn: async () => {
      const response = await fetch('/api/installations?forSiteSurvey=true', {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch Won customers: ${response.status}`);
      }

      const installations = await response.json();

      // Transform installation data to flat customer format for scheduling UI
      return installations.map((inst: any) => ({
        id: inst.customerId || inst.id,
        installationId: inst.id,
        firstName: inst.customer?.firstName || '',
        lastName: inst.customer?.lastName || '',
        email: inst.customer?.email || '',
        phone: inst.customer?.phone || '',
        address: inst.customer?.address || inst.installationAddress || '',
        postcode: inst.customer?.postcode || '',
        installationCount: inst.installationCount || 0,
      }));
    },
    retry: 2,
    staleTime: 30000, // Cache for 30 seconds
  });

  // Fetch staff for assignment
  const { data: staff = [] } = useStaff();

  const handleCreateJob = async () => {
    if (!selectedCustomer || !jobDate || selectedStaff.length === 0) {
      alert('Please select a customer, date, and at least one staff member');
      return;
    }

    try {
      // Create job schedule
      const jobResponse = await api.post('/api/scheduling/jobs', {
        customerId: selectedCustomer.id,
        installationId: selectedCustomer.installationId || null,
        scheduledDate: jobDate,
        scheduledStartTime: startTime,
        scheduledEndTime: endTime,
        status: 'scheduled',
        crewSizeRequired: selectedStaff.length
      });

      const jobId = jobResponse.data.id;

      // Assign staff to the job
      for (const staffId of selectedStaff) {
        await api.post(`/api/scheduling/jobs/${jobId}/assign`, {
          staffId,
          roleAssigned: 'installer', // Default role, can be customized
          status: 'assigned'
        });
      }

      alert('Job created and staff assigned successfully!');
      onClose();
      window.location.reload(); // Refresh to show new job
    } catch (error: any) {
      console.error('Failed to create job:', error);
      alert(`Failed to create job: ${error.response?.data?.message || error.message}`);
    }
  };

  return (
    <div className="space-y-6">
      {/* Customer Selection */}
      <div>
        <h3 className="text-lg font-semibold mb-3">Select Customer</h3>
        {customersError && (
          <div className="text-center py-8 text-red-600">
            <p>Error loading customers: {(customersError as Error).message}</p>
          </div>
        )}
        {customersLoading ? (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-blue-500 border-t-transparent" />
          </div>
        ) : wonCustomers.length === 0 ? (
          <div className="text-center py-8 text-slate-600">
            <p>No Won customers available</p>
            <p className="text-sm mt-2">Customers with stage "Won" will appear here</p>
            <p className="text-xs mt-2 text-slate-500">Data: {JSON.stringify(wonCustomers)}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3 max-h-60 overflow-y-auto">
            {wonCustomers.map((customer: any) => (
              <Card
                key={customer.id}
                className={`cursor-pointer transition-all ${
                  selectedCustomer?.id === customer.id
                    ? 'border-2 border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'hover:border-blue-300'
                }`}
                onClick={() => setSelectedCustomer(customer)}
              >
                <CardContent className="p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="font-semibold">
                        {customer.firstName} {customer.lastName}
                      </h4>
                      <p className="text-sm text-slate-600 dark:text-slate-400">
                        {customer.email}
                      </p>
                      {customer.address && (
                        <p className="text-sm text-slate-500 mt-1">{customer.address}</p>
                      )}
                    </div>
                    {selectedCustomer?.id === customer.id && (
                      <CheckCircle2 className="w-5 h-5 text-blue-600" />
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Schedule Details */}
      {selectedCustomer && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Schedule Details</h3>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Date</label>
              <input
                type="date"
                value={jobDate}
                onChange={(e) => setJobDate(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Start Time</label>
              <input
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">End Time</label>
              <input
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
          </div>
        </div>
      )}

      {/* Staff Assignment */}
      {selectedCustomer && jobDate && (
        <div>
          <h3 className="text-lg font-semibold mb-3">Assign Staff</h3>
          <div className="grid grid-cols-1 gap-2 max-h-48 overflow-y-auto">
            {staff.filter((s: any) => s.isActive).map((member: any) => (
              <Card
                key={member.id}
                className={`cursor-pointer transition-all ${
                  selectedStaff.includes(member.id)
                    ? 'border-2 border-green-500 bg-green-50 dark:bg-green-900/20'
                    : 'hover:border-green-300'
                }`}
                onClick={() => {
                  setSelectedStaff(prev =>
                    prev.includes(member.id)
                      ? prev.filter(id => id !== member.id)
                      : [...prev, member.id]
                  );
                }}
              >
                <CardContent className="p-3">
                  <div className="flex justify-between items-center">
                    <div>
                      <span className="font-medium">
                        {member.user?.fullName || member.user?.username}
                      </span>
                      <span className="text-sm text-slate-600 dark:text-slate-400 ml-2">
                        ({member.jobRole?.replace('_', ' ')})
                      </span>
                    </div>
                    {selectedStaff.includes(member.id) && (
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          <p className="text-sm text-slate-600 dark:text-slate-400 mt-2">
            Selected: {selectedStaff.length} staff member(s)
          </p>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex justify-end gap-3 pt-4 border-t">
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button
          onClick={handleCreateJob}
          disabled={!selectedCustomer || !jobDate || selectedStaff.length === 0}
          className="bg-gradient-to-r from-blue-600 to-indigo-600"
        >
          Create Job
        </Button>
      </div>
    </div>
  );
}
